import { DATE_FORMATS, HTMLTagRegEx } from "../../../constants";
import { DurationUnit } from "../../../types";
import { programDetailsConfig } from "../constants";
import {
  IBranchBackEndModel,
  IOtherFeeBack,
  IProgramBackEndModel,
  IProgramFrontEndModel,
} from "../types";

export function parseToProgramBackEndModel(
  programFrontEndModel: IProgramFrontEndModel,
): IProgramBackEndModel {
  const branches: IBranchBackEndModel[] = programFrontEndModel.branches.map(
    (branch) => ({ branchId: branch }),
  );
  Object.values(programFrontEndModel.branchesWithAppCount || {}).forEach(
    (branch) => {
      if (
        branch.deactivationNote &&
        !branches.some((b) => b.branchId === branch.branchId)
      ) {
        branches.push({
          branchId: branch.branchId,
          deactivationNote: {
            text: branch.deactivationNote.text,
          },
        });
      }
    },
  );

  const programBackEndModel: IProgramBackEndModel = {
    id: programFrontEndModel.id,
    majorId: programFrontEndModel.majorId,
    programType: +programFrontEndModel.programType,
    housingTypes: programFrontEndModel.housingTypes,
    intakes: [],
    exams: [],
    details: [],
    detailFiles: [],
    restrictedCountries: [],
    branches: branches,
    durationUnit: programFrontEndModel.durationUnit.id,
    durationAmount: +programFrontEndModel.durationAmount,
    tuitionDurationUnit: programFrontEndModel.tuitionDurationUnit.id,
    tuitionDurationAmount:
      programFrontEndModel.tuitionDurationUnit.id === DurationUnit.FULL
        ? null
        : +programFrontEndModel.tuitionDurationAmount!,
    boardingAmount: programFrontEndModel.boardingAmount
      ? +programFrontEndModel.boardingAmount
      : null,
    homestayAmount: programFrontEndModel.homestayAmount
      ? +programFrontEndModel.homestayAmount
      : null,
    insuranceAmount: programFrontEndModel.insuranceAmount
      ? +programFrontEndModel.insuranceAmount
      : null,

    hasTuitionScholarship: !!programFrontEndModel.hasTuitionScholarship,
    hasTuitionFinancialAid: !!programFrontEndModel.hasTuitionFinancialAid,
    hasOtherFeesScholarship: !!programFrontEndModel.hasOtherFeesScholarship,
    hasOtherFeesFinancialAid: !!programFrontEndModel.hasOtherFeesFinancialAid,
    tuition: +programFrontEndModel.tuition,
    currency: programFrontEndModel.currency.id,
    commissionAmount: +programFrontEndModel.commissionAmount,
    commissionType: programFrontEndModel.commissionType.id,
  };

  // Intakes --------------------------------------------------
  [
    ...(programFrontEndModel.existingIntakes || []),
    ...(programFrontEndModel.defaultNewIntakes || []),
    ...(programFrontEndModel.newIntakes || []),
  ].forEach((intake) => {
    if (intake.isActive === false) {
      if (intake.deactivationNote?.text) {
        programBackEndModel.intakes.push({
          deadline: intake.deadline.format(DATE_FORMATS.frontEnd),
          startDate: intake.startDate.format(DATE_FORMATS.frontEnd),
          deactivationNote: { text: intake.deactivationNote?.text },
          isDeleted: intake.isDeleted,
        });
      } else {
        programBackEndModel.intakes.push({
          deadline: intake.deadline.format(DATE_FORMATS.frontEnd),
          startDate: intake.startDate.format(DATE_FORMATS.frontEnd),
          deactivationNote: { text: "" },
          isDeleted: intake.isDeleted,
        });
      }
    } else {
      programBackEndModel.intakes.push({
        deadline: intake.deadline.format(DATE_FORMATS.frontEnd),
        startDate: intake.startDate.format(DATE_FORMATS.frontEnd),
        isDeleted: intake.isDeleted,
      });
    }
  });

  //  Exams ---------------------------------------------------
  [
    ...(programFrontEndModel.mainExams || []),
    ...(programFrontEndModel.addedExams || []),
  ].forEach((exam) => {
    if (exam?.name) {
      programBackEndModel.exams.push({
        name: exam.name.trim(),
        score: exam.score!.trim(),
      });
    }
  });

  //Details
  Object.entries(programFrontEndModel.details).forEach(([key, value]) => {
    const typeId = +key.split("_")[1];
    const typeName = programDetailsConfig
      .find((i) => i.type === typeId)
      ?.name?.trim();

    if (value && String(value).replace(HTMLTagRegEx, "").trim().length) {
      programBackEndModel.details.push({
        type: typeId,
        typeName,
        content: String(value),
      });
    }
  });

  //Files
  if (programFrontEndModel.detailFiles?.length) {
    programBackEndModel.detailFiles = programFrontEndModel.detailFiles.map(
      (file) => ({
        fileId: file.id,
      }),
    );
  } else {
    programBackEndModel.detailFiles = [];
  }

  // Restricted Countries -------------------------------------
  if (programFrontEndModel.restrictedCountries?.length) {
    programBackEndModel.restrictedCountries =
      programFrontEndModel.restrictedCountries.map((country) => country?.code);
  }

  // Other Fees -----------------------------------------------
  const otherFees: IOtherFeeBack[] = [];

  [
    ...(programFrontEndModel.mainOtherFees || []),
    ...(programFrontEndModel.addedOtherFees || []),
  ].forEach((fee) => {
    if (fee?.name && fee?.amount) {
      otherFees.push({
        name: fee.name.trim(),
        amount: +fee.amount,
      });
    }
  });

  programBackEndModel.otherFees = otherFees;

  if (programFrontEndModel.tuitionScholarshipAmount) {
    programBackEndModel.tuitionScholarshipAmount =
      +programFrontEndModel.tuitionScholarshipAmount;
  }

  if (programFrontEndModel.tuitionScholarshipType) {
    programBackEndModel.tuitionScholarshipType =
      programFrontEndModel.tuitionScholarshipType.id;
  }

  if (programFrontEndModel.tuitionFinancialAidAmount) {
    programBackEndModel.tuitionFinancialAidAmount =
      +programFrontEndModel.tuitionFinancialAidAmount;
  }

  if (programFrontEndModel.tuitionFinancialAidType) {
    programBackEndModel.tuitionFinancialAidType =
      programFrontEndModel.tuitionFinancialAidType.id;
  }

  if (programFrontEndModel.otherFeesScholarshipAmount) {
    programBackEndModel.otherFeesScholarshipAmount =
      +programFrontEndModel.otherFeesScholarshipAmount;
  }

  if (programFrontEndModel.otherFeesScholarshipType) {
    programBackEndModel.otherFeesScholarshipType =
      programFrontEndModel.otherFeesScholarshipType.id;
  }

  if (programFrontEndModel.otherFeesFinancialAidAmount) {
    programBackEndModel.otherFeesFinancialAidAmount =
      +programFrontEndModel.otherFeesFinancialAidAmount;
  }

  if (programFrontEndModel.otherFeesFinancialAidType) {
    programBackEndModel.otherFeesFinancialAidType =
      programFrontEndModel.otherFeesFinancialAidType.id;
  }

  return programBackEndModel;
}

export function mergeProgramSteps(data) {
  return {
    id: data.id,
    ...data.step1,
    ...data.step2,
    ...data.step3,
    ...data.step4,
    ...{ majorId: data.step1.major?.id },
  };
}
